import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getClosest, propagateEvent} from 'common/util/dom';

import {ContextPanelInternal} from './internal';
import template from './index.jade';
import css from './index.styl?inline';

const PLACEMENT = {
  TOP: `top`,
  RIGHT: `right`,
  BOTTOM: `bottom`,
  LEFT: `left`,
};

export class ContextPanel extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      ...ContextPanelInternal.attrsSchema,
      'position-parent-selector': `string`,
      'relative-parent-positioning': {
        type: `boolean`,
        description: `When set, the context panel positioning is left untouched and positions itself relative to the nearest relative ancestor.`,
      },
      placement: {type: `string`, default: PLACEMENT.RIGHT, enum: Object.values(PLACEMENT)},
      'force-placement': `boolean`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      helpers: {
        propagateEvent: (ev) => propagateEvent(this, ev),
        actionClick: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: ev.detail,
            }),
          );
        },
        changeDescription: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextEditDescription`, {
              composed: true,
              cancelable: true,
              detail: ev.detail,
            }),
          );
        },
      },
    };
  }

  setUpTooltip() {
    this.tooltip = document.createElement(`mp-context-panel-internal`);
    this.tooltip.addEventListener(`actionClick`, this.helpers.actionClick);
    this.tooltip.addEventListener(`changeDescription`, this.helpers.changeDescription);
    this.tooltip.addEventListener(`hoverIn`, this.helpers.propagateEvent);
    this.tooltip.addEventListener(`hoverOut`, this.helpers.propagateEvent);
    this.tooltip.setAttribute(`tooltip-data`, this.getAttribute(`tooltip-data`));
    document.body.appendChild(this.tooltip);
    window.addEventListener(`resize`, this.onResize);
  }

  tearDownTooltip() {
    if (this.tooltip) {
      if (document.body.contains(this.tooltip)) {
        document.body.removeChild(this.tooltip);
      }
      this.tooltip = null;
    }

    this.cancelTooltipPositioningRaf();
  }

  connectedCallback() {
    super.connectedCallback();
    // If the user passes in an element passed in by props, use it for positioning.
    // Otherwise fallback to using the element defined by the selector
    const positionElementSelector = this.attr(`position-parent-selector`);
    this.positionElement =
      this.tooltipPositionParentElement || getClosest(this, positionElementSelector) || this.previousSibling;

    this.onResize = () => this.setTooltipPositioningRaf();

    if (this.attr(`tooltip-data`) && !this.tooltip && !this.attr(`relative-parent-positioning`)) {
      // lazy create internal tooltip elem when there is data to be displayed
      this.setUpTooltip();
      this.setTooltipPositioningRaf();
    }
  }

  disconnectedCallback() {
    this.tearDownTooltip();
    super.disconnectedCallback();
    window.removeEventListener(`resize`, this.onResize);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (
      this.initialized &&
      attr === `tooltip-data` &&
      this.attr(`tooltip-data`) &&
      !this.tooltip &&
      !this.attr(`relative-parent-positioning`)
    ) {
      // lazy create internal tooltip elem when there is data to be displayed
      this.setUpTooltip();
    }

    if (this.tooltip) {
      this.tooltip.setAttribute(attr, this.getAttribute(attr));
      // Need to wait an animation frame for the tooltip to be re-rendered with the new attributes
      this.setTooltipPositioningRaf();
    }
  }

  cancelTooltipPositioningRaf() {
    if (this.tooltipPositioningAnimationId) {
      cancelAnimationFrame(this.tooltipPositioningAnimationId);
      this.tooltipPositioningAnimationId = null;
    }
  }

  setTooltipPositioningRaf() {
    this.cancelTooltipPositioningRaf();
    this.tooltipPositioningAnimationId = requestAnimationFrame(() => {
      this.setTooltipPositioning();
      this.tooltipPositioningAnimationId = null;
    });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  setTooltipPositioning() {
    if (!this.positionElement || !this.attr(`tooltip-data`)) {
      return;
    }

    const rect = this.positionElement.getBoundingClientRect();
    const tooltipHeight = this.tooltip.offsetHeight;
    const tooltipWidth = this.tooltip.offsetWidth;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // ensure tooltip is displayed within viewport
    let placement = this.attr(`placement`);
    if (!this.attr(`force-placement`)) {
      if (placement === PLACEMENT.RIGHT) {
        placement = rect.right + tooltipWidth <= windowWidth ? PLACEMENT.RIGHT : PLACEMENT.LEFT;
      } else if (placement === PLACEMENT.LEFT) {
        placement = rect.left - tooltipWidth >= 0 ? PLACEMENT.LEFT : PLACEMENT.RIGHT;
      } else if (placement === PLACEMENT.TOP) {
        placement = rect.top - tooltipHeight >= 0 ? PLACEMENT.TOP : PLACEMENT.BOTTOM;
      } else if (placement === PLACEMENT.BOTTOM) {
        placement =
          // if there is enough room below OR there is not room on top, place it below
          rect.bottom + tooltipHeight <= windowHeight || rect.top - tooltipHeight < 0
            ? PLACEMENT.BOTTOM
            : PLACEMENT.TOP;
      }
    }

    if ([PLACEMENT.LEFT, PLACEMENT.RIGHT].includes(placement)) {
      const padding = 2;
      this.tooltip.style.top = `${window.scrollY + rect.top}px`;
      if (placement === PLACEMENT.LEFT) {
        this.tooltip.style.left = `${window.scrollX + rect.left - tooltipWidth - padding}px`;
      } else {
        this.tooltip.style.left = `${window.scrollX + rect.right + padding}px`;
      }
    } else if ([PLACEMENT.TOP, PLACEMENT.BOTTOM].includes(placement)) {
      const padding = 6;
      const isPastMidPoint = rect.left >= windowWidth / 2;
      const horizontalDelta = isPastMidPoint ? tooltipWidth - rect.width : 0;

      this.tooltip.style.left = `${window.scrollX + (rect.left - horizontalDelta)}px`;
      if (placement === PLACEMENT.TOP) {
        this.tooltip.style.top = `${window.scrollY + rect.top - tooltipHeight - padding}px`;
      } else {
        this.tooltip.style.top = `${window.scrollY + rect.bottom + padding}px`;
      }
    }
  }

  set tooltipPositionParentElement(el) {
    this.positionElement = el;
    if (this.tooltip) {
      this.setTooltipPositioning();
    }
  }
  get tooltipPositionParentElement() {
    return this.positionElement;
  }
}

export const ContextPanelEl = defineAndMakeJsxFactory(`mp-context-panel`, ContextPanel);
